import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useParams, useSearchParams } from 'react-router-dom';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import { getUserDonateSettingsByUserID } from '../../api';
import { getPublicAlertTiers } from '../../api/payment/donate-alert-tiers.api';
import { getPublicAlertStyle } from '../../api/payment/alert-style.api';
import DonateAlertView from '../../components/donate-alert/donate-alert-view';
import AlertFireworks from '../../components/donate-alert/alert-fireworks';
import { AlertStyle, DEFAULT_ALERT_STYLE, normalizeAlertStyle } from '../../utils/alert-style';
import { musicDonateText } from '../../utils/music-request';
import { AlertVolumes, DEFAULT_ALERT_VOLUMES, applyVolume, readAlertVolumes } from '../../utils/overlay-volume';
import { synthesizeSpeech } from '../../utils/public-tts';
import { DEFAULT_TTS_VOICE, KindFields, ResolvedKind, resolveAlertKind, spokenAlertText } from '../../utils/alert-kind';
import { IntroHandle, playIntro, routeAlertVolume } from '../../utils/alert-audio';
import {
  AlertStage,
  AlertTier,
  DEFAULT_ALERT_MS,
  DEFAULT_TEMPLATE,
  MAX_ALERT_MS,
  MAX_QUEUE,
  alertStages,
  isAllowedAssetUrl,
  renderTemplate,
  safeVoiceUrl,
  selectTier,
  shouldHardEnd,
  tierImageSrc,
  tierSoundSrc,
} from '../../utils/donate-alert';

type Donation = {
  senderUserName: string;
  donationValue: number;
  grossAmount?: number;
  message?: string;
  voiceUrl?: unknown;
  isMember?: boolean;
  /** Donate là tiền yêu cầu nhạc: bài đã có lớp phủ nhạc, không phát âm thanh hay đọc lại. */
  isMusicRequest?: boolean;
};

type Resolved = {
  key: number;
  gif: string;
  sound: string;
  color: string;
  text: string;
  message: string;
  isMember: boolean;
  readAloud: boolean;
  silent: boolean;
  minMs: number;
  hardEnd: boolean;
  style: AlertStyle;
  voiceUrl: string | null;
  stages: AlertStage[];
  /** Kiểu thông báo đã quy ra (Thường khi không khớp mốc hoặc donate yêu cầu nhạc). */
  kind: ResolvedKind;
  /** Câu máy đọc (cùng chữ với câu hiển thị; kiểu Đặc biệt chỉ thêm dấu ngắt nghỉ). */
  spoken: string;
};

// Query của chế độ xem thử để chỉnh giao diện tức thì (cardColor truyền không kèm dấu #).
const STYLE_QUERY_KEYS = ['layout', 'position', 'textSize', 'animation', 'cardColor', 'cardOpacity'] as const;

// Donate yêu cầu nhạc chỉ hiện chữ ngắn, không theo thời lượng của mốc.
const SILENT_ALERT_MS = 5000;

const DEFAULT_COLOR = 'red';

// Chế độ "Xem thử" của khu Cài đặt: kiểu và các lựa chọn riêng truyền qua query ("1" bật, "0" tắt, thiếu = theo kiểu).
const queryBool = (v: string | null): boolean | null => {
  if (v === '1') return true;
  if (v === '0') return false;
  return null;
};
const kindFromQuery = (q: URLSearchParams): KindFields => ({
  kind: q.get('kind'),
  introEnabled: queryBool(q.get('intro')),
  medalEnabled: queryBool(q.get('medal')),
  effectEnabled: queryBool(q.get('effect')),
  voice: q.get('voice'),
});
// Cài đặt cũ lưu sẵn "#000000" cho người chưa từng chọn màu; trước đây overlay luôn tô đỏ.
// Giữ đỏ cho trường hợp đó để stream nền tối không bị chữ đen.
const pickBaseColor = (c?: string | null) =>
  c && /^#[0-9a-fA-F]{6}$/.test(c) && c.toLowerCase() !== '#000000' ? c : DEFAULT_COLOR;

const PageDonateLivestream = () => {
  const { contentCreatorId } = useParams<{ contentCreatorId: string }>();
  const [searchParams] = useSearchParams();
  const isTest = searchParams.get('isTest') === 'true';

  const baseRef = useRef({ sound: 'sound_1.mp3', image: 'img_1.gif', color: DEFAULT_COLOR });
  const tiersRef = useRef<AlertTier[]>([]);
  const styleRef = useRef<AlertStyle>(DEFAULT_ALERT_STYLE);
  // Âm lượng streamer chỉnh trong Cài đặt Donate, đi cùng phản hồi giao diện (chưa chỉnh = 100, to như cũ).
  const volumesRef = useRef<AlertVolumes>(DEFAULT_ALERT_VOLUMES);
  const lastLoadRef = useRef(0);
  const loadChainRef = useRef<Promise<void>>(Promise.resolve());
  const queueRef = useRef<Donation[]>([]);
  const keyRef = useRef(0);
  const currentKeyRef = useRef<number | null>(null);
  const doneRef = useRef({ min: false, media: false });
  const soundDoneKeyRef = useRef<number | null>(null);
  const ttsRef = useRef<HTMLAudioElement | null>(null);
  const voiceRef = useRef<HTMLAudioElement | null>(null);
  // Dọn nút Web Audio / blob của các tiếng thuộc alert đang hiện (gọi khi alert kết thúc).
  const audioCleanupsRef = useRef<(() => void)[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [current, setCurrent] = useState<Resolved | null>(null);
  const [queueTick, setQueueTick] = useState(0);

  const loadTiers = useCallback((): Promise<void> => {
    if (!contentCreatorId) return Promise.resolve();
    return getPublicAlertTiers(contentCreatorId)
      .then((t) => {
        tiersRef.current = t || [];
        lastLoadRef.current = Date.now();
      })
      .catch(() => undefined);
  }, [contentCreatorId]);

  // Giao diện thông báo + âm lượng: lỗi mạng thì giữ bản đang có (lần đầu là mặc định = giao diện cũ, âm lượng 100).
  // Tải lại cùng nhịp với mốc (60 giây, và ngay trước donate thật nếu bản đang có cũ hơn 5 giây) nên streamer đổi
  // âm lượng thì thông báo kế tiếp đã dùng mức mới, không phải tải lại OBS.
  const loadStyle = useCallback((): Promise<void> => {
    if (!contentCreatorId) return Promise.resolve();
    return getPublicAlertStyle(contentCreatorId)
      .then((s) => {
        styleRef.current = normalizeAlertStyle(s);
        volumesRef.current = readAlertVolumes(s);
      })
      .catch(() => undefined);
  }, [contentCreatorId]);

  // Mốc và giao diện luôn tải cùng lúc (song song), chung mọi nhịp tải lại.
  const loadAlertConfig = useCallback(
    (): Promise<void> => Promise.all([loadTiers(), loadStyle()]).then(() => undefined),
    [loadTiers, loadStyle]
  );

  const resolve = useCallback(
    (d: Donation): Resolved => {
      const amount = Number(d.grossAmount || d.donationValue || 0);
      const base = baseRef.current;
      let { color } = base;
      let sound = `/sound/${base.sound}`;
      let gif = `/gif/${base.image}`;
      let template: string | null = null;
      let readAloud = true;
      let minMs = DEFAULT_ALERT_MS;
      let hardEnd = false;
      let style = styleRef.current;
      let voiceUrl: string | null = null;
      let kindFields: KindFields | null = null;

      if (isTest) {
        // Nút "Xem thử" truyền thẳng thông số qua query.
        const qs = searchParams.get('sound');
        if (qs) sound = `/sound/${qs}`;
        const qi = searchParams.get('image');
        if (qi) gif = `/gif/${qi}`;
        const qc = searchParams.get('colorText');
        if (qc) color = `#${qc.replace('#', '')}`;
        template = searchParams.get('template');
        const dur = Number(searchParams.get('duration'));
        if (dur >= 5 && dur <= 30) minMs = dur * 1000;
        readAloud = searchParams.get('readAloud') !== 'false';
        // File riêng (Dropbox) thắng nếu có, kể cả khi đã đổi bằng sound/image ở trên.
        const qsu = searchParams.get('soundUrl');
        if (isAllowedAssetUrl(qsu)) sound = qsu as string;
        const qiu = searchParams.get('imageUrl');
        if (isAllowedAssetUrl(qiu)) gif = qiu as string;
        // Giao diện: query nào có thì thắng bản đã lưu, giá trị lạ về mặc định qua normalize.
        const qStyle: Record<string, string> = {};
        STYLE_QUERY_KEYS.forEach((k) => {
          const v = searchParams.get(k);
          if (v !== null) qStyle[k] = k === 'cardColor' ? `#${v.replace('#', '')}` : v;
        });
        style = normalizeAlertStyle({ ...style, ...qStyle });
        voiceUrl = safeVoiceUrl(searchParams.get('voiceUrl'));
        kindFields = kindFromQuery(searchParams);
      } else {
        const tier = selectTier(tiersRef.current, amount);
        const tierMatched = !!tier;
        if (tier) {
          sound = tierSoundSrc(tier) || sound;
          gif = tierImageSrc(tier) || gif;
          color = tier.textColor || color;
          template = tier.template;
          readAloud = tier.readAloud;
          minMs = Math.min(Math.max(tier.durationSec, 5), 30) * 1000;
          kindFields = tier;
        }
        voiceUrl = safeVoiceUrl(d.voiceUrl);
        // Donate thật không khớp mốc nào: tắt đúng giờ mặc định dù âm thanh/tiếng đọc chưa xong —
        // TRỪ KHI có ghi âm kèm theo, lúc đó để ghi âm phát hết (chỉ còn MAX_ALERT_MS làm trần).
        hardEnd = shouldHardEnd({ tierMatched, hasVoice: !!voiceUrl });
      }

      // Câu hiển thị đã có sẵn lời nhắn (chứa {loi_nhan}) thì khỏi hiện thêm dòng lời nhắn riêng
      // và khỏi đọc lại lần hai.
      const silent = !!d.isMusicRequest;
      if (silent) {
        // Bỏ âm thanh mốc, đọc máy và ghi âm; chỉ giữ một dòng chữ ngắn.
        readAloud = false;
        voiceUrl = null;
        hardEnd = true;
        minMs = SILENT_ALERT_MS;
        kindFields = null;
      }
      // Kiểu thông báo: không khớp mốc nào thì Thường (giống hệt trước đây). Kiểu có thêm giây thì cộng vào thời gian hiện.
      const kind = resolveAlertKind(kindFields);
      if (!silent) minMs = Math.min(minMs + kind.extraMs, MAX_ALERT_MS);
      const effectiveTemplate = template || DEFAULT_TEMPLATE;
      const messageInText = effectiveTemplate.includes('{loi_nhan}');
      const message = silent || messageInText ? '' : d.message || '';

      keyRef.current += 1;
      return {
        key: keyRef.current,
        gif,
        sound,
        color,
        text: silent
          ? musicDonateText(d.message)
          : renderTemplate(template, { ten: d.senderUserName, soTien: amount, loiNhan: d.message }),
        message,
        isMember: !!d.isMember,
        readAloud,
        silent,
        minMs,
        hardEnd,
        style,
        voiceUrl,
        stages: alertStages({ readAloud, hasVoice: !!voiceUrl }),
        kind,
        spoken: spokenAlertText({
          template,
          ten: d.senderUserName,
          soTien: amount,
          loiNhan: d.message,
          message,
          hasVoice: !!voiceUrl,
          pauses: kind.pauses,
        }),
      };
    },
    [isTest, searchParams]
  );

  const enqueue = useCallback((d: Donation) => {
    if (queueRef.current.length >= MAX_QUEUE) return; // quá tải thì bỏ bớt, không làm treo overlay
    queueRef.current.push(d);
    setQueueTick((t) => t + 1);
  }, []);

  // Donate thật: nếu danh sách mốc đã tải cách đây quá 5 giây thì tải lại trước khi xếp hàng,
  // để "Gửi thử lên OBS" ngay sau khi lưu không dùng mốc cũ trên một OBS đang mở sẵn; timeout 1.5s
  // để API chậm không làm chậm alert. Nối qua loadChainRef để nhiều donate dồn dập vẫn phát đúng
  // thứ tự đến (mỗi lượt chờ nối tiếp lượt trước, không chạy song song làm lệch thứ tự).
  const handleDonation = useCallback(
    (d: Donation) => {
      const stale = Date.now() - lastLoadRef.current > 5000;
      const refresh = stale
        ? Promise.race([
            loadAlertConfig(),
            new Promise<void>((resolve) => {
              setTimeout(resolve, 1500);
            }),
          ])
        : Promise.resolve();
      loadChainRef.current = loadChainRef.current.then(() => refresh).then(() => enqueue(d));
    },
    [loadAlertConfig, enqueue]
  );

  const runAudioCleanups = useCallback(() => {
    const list = audioCleanupsRef.current;
    audioCleanupsRef.current = [];
    list.forEach((fn) => {
      try {
        fn();
      } catch {
        // bỏ qua
      }
    });
  }, []);

  const finish = useCallback(() => {
    if (ttsRef.current) {
      ttsRef.current.pause();
      ttsRef.current = null;
    }
    if (voiceRef.current) {
      voiceRef.current.pause();
      voiceRef.current = null;
    }
    runAudioCleanups();
    currentKeyRef.current = null;
    setCurrent(null);
  }, [runAudioCleanups]);

  // Dọn audio TTS/ghi âm nếu overlay bị gỡ giữa chừng (đổi trang, đóng tab OBS source…).
  useEffect(
    () => () => {
      if (ttsRef.current) {
        ttsRef.current.pause();
        ttsRef.current = null;
      }
      if (voiceRef.current) {
        voiceRef.current.pause();
        voiceRef.current = null;
      }
      runAudioCleanups();
    },
    [runAudioCleanups]
  );

  const tryFinish = useCallback(() => {
    if (doneRef.current.min && doneRef.current.media) finish();
  }, [finish]);

  const markMediaDone = useCallback(
    (key: number) => {
      if (currentKeyRef.current !== key) return;
      doneRef.current.media = true;
      tryFinish();
    },
    [tryFinish]
  );

  // Chạy lần lượt các bước "tiếng" của alert (đọc máy rồi phát ghi âm, tuỳ alert.stages).
  // Mỗi bước tự chặn gọi lại hai lần (onended/onerror của Audio có thể bắn cả hai), và kiểm
  // currentKeyRef trước khi làm gì để một callback trễ của alert CŨ không đẩy alert MỚI chạy tiếp.
  const runStage = useCallback(
    (alert: Resolved, stageIdx: number) => {
      if (currentKeyRef.current !== alert.key) return;
      const stage = alert.stages[stageIdx];
      if (!stage) {
        markMediaDone(alert.key);
        return;
      }
      let advanced = false;
      const advance = () => {
        if (advanced) return;
        advanced = true;
        runStage(alert, stageIdx + 1);
      };

      // Giọng đọc và ghi âm: kiểu Thường đặt .volume như trước; kiểu to hơn thì cộng điểm của kiểu (Web Audio nếu vượt 100%).
      const playVoiceAudio = (el: HTMLAudioElement) => {
        const media = el;
        media.onended = advance;
        media.onerror = advance;
        const { boost } = alert.kind;
        if (boost === 0) {
          applyVolume(media, volumesRef.current.voiceVolume);
          media.play().catch(advance);
          return;
        }
        routeAlertVolume(media, volumesRef.current.voiceVolume, boost)
          .catch(() => () => undefined)
          .then((cleanup) => {
            if (currentKeyRef.current !== alert.key) {
              cleanup();
              return;
            }
            audioCleanupsRef.current.push(cleanup);
            media.play().catch(advance);
          });
      };

      if (stage === 'tts') {
        const { voice: ttsVoice, rate: ttsRate } = alert.kind;
        const opts =
          ttsVoice === DEFAULT_TTS_VOICE && ttsRate === 1 ? undefined : { voice: ttsVoice, speakingRate: ttsRate };
        (opts ? synthesizeSpeech(alert.spoken, opts) : synthesizeSpeech(alert.spoken)).then((tts) => {
          if (!tts || currentKeyRef.current !== alert.key) {
            advance();
            return;
          }
          ttsRef.current = tts;
          playVoiceAudio(tts);
        });
        return;
      }

      // stage === 'voice'
      const voice = new Audio(alert.voiceUrl as string);
      voiceRef.current = voice;
      playVoiceAudio(voice);
    },
    [markMediaDone]
  );

  // Âm thanh mốc xong (hoặc lỗi) thì chạy các bước tiếng còn lại (đọc máy/ghi âm).
  // Có thể bị gọi 2 lần cho CÙNG alert (onError của <audio> + play().catch() trong effect hẹn giờ
  // khi tải lỗi thường bắn cả 2); chặn bằng soundDoneKeyRef để chỉ lượt gọi đầu tiên chạy tiếp.
  const onSoundDone = useCallback(
    (alert: Resolved) => {
      if (currentKeyRef.current !== alert.key) return;
      if (soundDoneKeyRef.current === alert.key) return;
      soundDoneKeyRef.current = alert.key;
      if (alert.stages.length === 0) {
        markMediaDone(alert.key);
        return;
      }
      runStage(alert, 0);
    },
    [markMediaDone, runStage]
  );

  // Lấy alert kế tiếp khi đang rảnh.
  useEffect(() => {
    if (current || queueRef.current.length === 0) return;
    const next = queueRef.current.shift() as Donation;
    const resolved = resolve(next);
    currentKeyRef.current = resolved.key;
    doneRef.current = { min: false, media: false };
    soundDoneKeyRef.current = null;
    setCurrent(resolved);
  }, [current, queueTick, resolve]);

  // Hẹn giờ cho alert đang hiện: tối thiểu minMs, trần MAX_ALERT_MS để không bao giờ kẹt.
  useEffect(() => {
    if (!current) return undefined;
    const alert = current;
    const minTimer = setTimeout(() => {
      if (alert.hardEnd) {
        // Không khớp mốc nào: tắt đúng giờ mặc định, không chờ âm thanh/tiếng đọc xong.
        finish();
        return;
      }
      doneRef.current.min = true;
      tryFinish();
    }, alert.minMs);
    const maxTimer = setTimeout(finish, MAX_ALERT_MS);
    const el = audioRef.current;
    const { boost, intro } = alert.kind;
    let cancelled = false;
    let introHandle: IntroHandle | null = null;
    const ac = new AbortController();
    const startSound = () => {
      if (cancelled || currentKeyRef.current !== alert.key) return;
      if (el) el.play().catch(() => onSoundDone(alert));
      else onSoundDone(alert);
    };
    if (boost === 0 && !intro) {
      // Thường: y như trước.
      if (el) applyVolume(el, volumesRef.current.alertVolume);
      startSound();
    } else {
      // Nổi bật/Đặc biệt: nhạc mở màn chạy song song với việc chuẩn bị âm lượng (tải file Dropbox nếu cần), xong cả hai mới
      // phát tiếng thông báo. Chuẩn bị hỏng thì vẫn phát với .volume; nhạc mở màn hỏng thì bỏ qua.
      const { alertVolume } = volumesRef.current;
      const prepare = el
        ? routeAlertVolume(el, alertVolume, boost, ac.signal)
            .then((cleanup) => {
              if (cancelled) cleanup();
              else audioCleanupsRef.current.push(cleanup);
            })
            .catch(() => applyVolume(el, alertVolume))
        : Promise.resolve();
      introHandle = playIntro(intro, alertVolume, boost);
      Promise.all([prepare, introHandle.done.catch(() => undefined)]).then(startSound, startSound);
    }
    return () => {
      cancelled = true;
      ac.abort();
      introHandle?.stop();
      clearTimeout(minTimer);
      clearTimeout(maxTimer);
    };
  }, [current, finish, onSoundDone, tryFinish]);

  // Cài đặt cũ + mốc; mốc tự tải lại mỗi 60s để creator đổi giữa stream không phải mở lại OBS.
  // Chế độ "Xem thử" chỉ đẩy alert giả SAU KHI cài đặt cũ tải xong (dù thành công hay lỗi) để
  // màu/âm thanh mặc định giống hệt lúc phát thật, không phải giá trị khởi tạo tạm.
  useEffect(() => {
    if (!contentCreatorId) return undefined;
    // Không bao giờ lỗi; chờ tối đa 1.5s để API chậm không làm treo bản xem thử.
    const configReady = Promise.race([
      loadAlertConfig(),
      new Promise<void>((resolve) => {
        setTimeout(resolve, 1500);
      }),
    ]);
    getUserDonateSettingsByUserID(contentCreatorId)
      .then((res) => {
        if (!res) return;
        baseRef.current = {
          sound: res.sound || baseRef.current.sound,
          image: res.image || baseRef.current.image,
          color: pickBaseColor(res.colorText),
        };
      })
      .catch(() => undefined)
      .finally(() => {
        // Chờ cả giao diện đã lưu để bản xem thử dùng đúng khung/vị trí.
        if (isTest) {
          configReady.then(() => enqueue({ senderUserName: 'Ronaldo', donationValue: 1000000, message: '' }));
        }
      });
    const timer = setInterval(loadAlertConfig, 60000);
    return () => clearInterval(timer);
  }, [contentCreatorId, loadAlertConfig, isTest, enqueue]);

  // Kết nối kênh donate (không dùng cho chế độ xem thử).
  useEffect(() => {
    if (!contentCreatorId || isTest) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    connection.on('ReceiveDonationInfo', handleDonation);
    connection.onreconnected(() => loadAlertConfig());
    connection.start().catch((error) => console.error('Connection failed: ', error));
    return () => {
      connection.stop();
    };
  }, [contentCreatorId, isTest, handleDonation, loadAlertConfig]);

  if (!current) return null;

  return (
    <DonateAlertView
      gif={current.gif}
      text={current.text}
      message={current.message}
      isMember={current.isMember}
      color={current.color}
      style={current.style}
      animKey={current.key}
      medal={current.kind.medal}
      glow={current.kind.effect === 'glow'}
    >
      {current.kind.effect === 'fireworks' && <AlertFireworks key={`fw-${current.key}`} durationMs={current.minMs} />}
      {!current.silent && (
        <audio
          key={`snd-${current.key}`}
          ref={audioRef}
          src={current.sound}
          onEnded={() => onSoundDone(current)}
          onError={() => onSoundDone(current)}
        >
          <track kind="captions" />
        </audio>
      )}
    </DonateAlertView>
  );
};

export default PageDonateLivestream;
